import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TenantCampaignOrdersChartResponseDto } from '@/generated/korza-api-client/types.gen';
import { type ChartGranularity } from '@/lib/chart-axis';
import { bucketSumChartData } from '@/lib/hooks/use-chart-data';
import { useChartTooltip } from '@/lib/hooks/use-chart-tooltip';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import type { ChartRange } from '@/lib/partial-buckets';
import { MetricsBarChart } from '@/components/ui/charts/bar-chart';
import { MetricsChartBarSeries } from '@/components/ui/charts/chart-bar-series';
import { ChartClickHint } from '@/components/ui/charts/chart-click-hint';

type CampaignOrdersPoint =
  TenantCampaignOrdersChartResponseDto['points'][number];

interface CampaignOrdersCountChartProps {
  granularity: ChartGranularity;
  points: CampaignOrdersPoint[];
  /** The filter's period: hatches the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  syncId?: string;
  syncMethod?: 'index' | 'value';
  /**
   * When provided, each period is clickable; `bucketStart` is the point's
   * original api date.
   */
  onBucketClick?: (bucketStart: string) => void;
}

export function CampaignOrdersCountChart({
  granularity,
  points,
  range,
  syncId,
  syncMethod,
  onBucketClick,
}: CampaignOrdersCountChartProps) {
  const { LL, locale } = useI18nContext();

  const rows = useMemo(
    () =>
      bucketSumChartData(
        points,
        granularity,
        locale,
        (p) => p.ordersCount,
        'ordersCount'
      ),
    [granularity, locale, points]
  );
  const partial = usePartialBuckets(rows, range, granularity);
  const chartData = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        partial: partial.indexes.includes(index),
      })),
    [rows, partial.indexes]
  );

  const handleBucketClick = onBucketClick
    ? (row: Record<string, unknown>) => {
        if (typeof row.date === 'string') onBucketClick(row.date);
      }
    : undefined;

  const tooltipContent = useChartTooltip({
    footer: onBucketClick ? <ChartClickHint /> : undefined,
    formatRows: (_label, payload) => [
      {
        label: LL.metricsPage.charts.campaignOrdersCount.title(),
        value: new Intl.NumberFormat(locale).format(
          (payload[0]?.value as number) ?? 0
        ),
      },
    ],
  });

  return (
    <MetricsBarChart
      data={chartData}
      partialLegend={partial.edges}
      syncId={syncId}
      syncMethod={syncMethod}
      tooltipContent={tooltipContent}
      onBucketClick={handleBucketClick}
    >
      <MetricsChartBarSeries
        dataKey="ordersCount"
        partialIndexes={partial.indexes}
        partialLabels={partial.labels}
      />
    </MetricsBarChart>
  );
}
